import React, { useState } from 'react'
import useFetch from "../utils/useFetch"
import styled from "styled-components";
import Button from '../shared/Button';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Fade from '@mui/material/Fade';
import '../assets/styles/_rules_page.scss';
import AppLoader from "../shared/AppLoader";
import useAPI from "../utils/api";
import ConfirmationDialog from './Modals/ConfirmationDialog';
import CreateRuleDialog from "./Modals/CreateRuleDialog";
import { getTitleValue } from '../utils/ruleConditions'
import { useRoleCheck } from '../utils/useRoleCheck';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip { ...props } classes={ { popper: className } }/>
))(({ theme }) => ({
  [`& .${ tooltipClasses.tooltip }`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    marginTop: '0px !important',
    marginLeft: '15px',
    fontSize: 12,
    border: '1px solid #dadde9',
    maxWidth: '400px',
    
    'ul': {
      paddingLeft: '20px',
      paddingTop: '10px',
      paddingBottom: '10px'
    },
    'ul li': {
      listStyleType: 'disc'
    }
  }
}));

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-top: 24px;
  
  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    
    th {
      padding: 16px 20px;
      text-transform: uppercase;
      font-size: 13px;
      letter-spacing: 0.5px;
      font-weight: 600;
      color: white;
      background-color: #ED5A29;
      border-bottom: 2px solid #f0f0f0;
      text-align: center;
    }
    
    th:first-child {
      border-top-left-radius: 8px;
    }
    
    th:last-child {
      border-top-right-radius: 8px;
    }
    
    td {
      padding: 16px 20px;
      text-align: center;
      border-bottom: 1px solid #f5f5f5;
      vertical-align: middle;
    }
  }
  
  .empty-state {
    padding: 40px 0;
    text-align: center;
    color: #6c757d;
    margin: 0 auto;
    
    svg {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
  }
`;

const RuleCount = styled.span`
  display: inline-block;
  background: rgba(237, 90, 41, 0.1);
  color: #ED5A29;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 13px;
`;

const RuleName = styled.div`
  font-weight: 500;
  color: #212529;
  text-align: center;
`;

const RuleDescription = styled.div`
  color: #495057;
  cursor: default;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 auto;
`;

const ActionWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  
  button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background-color: rgba(237, 90, 41, 0.1);
      color: #ED5A29;
    }
    
    svg {
      font-size: 20px;
    }
  }
`;

const ViewsBar = styled.div`
  background: white;
  padding: 10px;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const Rules = ({ site }) => {
  const [update, setUpdating] = useState(false);
  const { loading, status, data } = useFetch('rules', update);
  const API = useAPI();
  const handleRoleCheck = useRoleCheck(API.currentRole);

  const deleteRule = async (rule) => {
    // To check allowed role to perform action
    if (handleRoleCheck()) { return; }
    await API.deleteRule(rule);
    setUpdating(!update);
    try {
      window.analytics?.track('Rule Deleted', {
        rule_name: rule.name,
        conditions_count: rule.conditions?.length,
      });
    } catch (_) {}
  }
  
  const deleteConfirmation = ({ onClick }) => (
    <button onClick={ onClick }><DeleteIcon/></button>
  )
  
  return (<div id="rule-lists">
    <div className="page-header">
      <h5>Rules</h5>
      <CreateRuleDialog onUpdate={(resp) => setUpdating(!update)}
                        handler={<Button color="secondary" label="Create New Rule"/>}/>
    </div>
    
    <TableWrapper>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Active Popups</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
        {loading ? (
          <tr>
            <td colSpan="4" style={{textAlign: 'center', padding: '30px 0'}}>
              Loading rules...
            </td>
          </tr>
        ) : status === 'fetched' && data && data.length > 0 ? (
          data.map((rule) => (
            <tr key={rule.id}>
              <td>{rule.name || "—"}</td>
              <td>
                {rule.conditions && rule.conditions.length ? (
                  rule.conditions.length < 2 ? 
                    getTitleValue(rule) || "—" :
                    <LightTooltip
                      title={getTitleValue(rule).join(", ")}
                      placement="bottom-start"
                      TransitionComponent={Fade}
                      TransitionProps={{timeout: 600}}>
                      <span>
                        {getTitleValue(rule)[0]} & {getTitleValue(rule).length - 1} others
                      </span>
                    </LightTooltip>
                ) : "—"}
              </td>
              <td>
                <RuleCount>{rule.popups_count || 0}</RuleCount>
              </td>
              <td>
                <ActionWrapper>
                  <CreateRuleDialog 

                    onUpdate={(resp) => setUpdating(!update)} 
                    handler={<button title="Edit Rule"><EditIcon/></button>}
                    ruleData={rule} 
                    updatingRule={true}
                  />
                  <CreateRuleDialog 
                    onUpdate={(resp) => setUpdating(!update)}
                    handler={<button title="Duplicate Rule"><ContentCopyIcon/></button>} 
                    ruleData={rule}
                  />
                  <ConfirmationDialog
                    onConfirm={() => deleteRule(rule)}
                    component={deleteConfirmation}
                  >
                    Are you sure you want to delete this rule? <br/>
                    {rule.popups_count !== 0 && 'Note: This rule has popups attached, all the popups will be deleted if you proceed.'}
                  </ConfirmationDialog>
                </ActionWrapper>
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="4" style={{textAlign: 'center', padding: '30px 0'}}>
              No rules found. Create your first rule to get started!
            </td>
          </tr>
        )}
        </tbody>
      </table>
    </TableWrapper>
    {loading && <AppLoader visible={true}/>}
  </div>)
}

export default Rules
